import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { SpaceProps } from 'styled-system';
import { flexWrap, space } from 'styled-system';

import { ProductEvent } from 'common/enums/Tracking';
import { useMatchesViewport } from 'common/hooks/media';
import { textWithEllipsis } from 'common/theme/styles';
import { DrawerInteraction, Element } from 'common/types/tracking';
import { ENERGY_EFFICIENCY_HASH } from '../../../constants/energyClass';
import { RatingSize } from '../../../enums/rating';
import { useLocalization } from '../../../providers/LocaleProvider';
import type { ArticleLabelsList } from '../../../types/labels';
import type { Product } from '../../../types/product-card';
import { Flex } from '../../Flex';
import ArticleSmallPrice, { RenderingStrategy } from '../../Price/ArticleSmallPrice';
import Rating from '../../Rating';
import { HeadlineH6, SubtitleST4 } from '../../Typography';
import ProductVariations from '../../Variations/components/ProductVariations';
import { getProductCardDetails } from '../utils/get-product-card-details';
import { CampaignBadge } from './CampaignBadge';
import { NameBlock } from './NameBlock';

interface ProductCardDetailsProps {
  article: Product;
  productIndex: number;
  visibleProductsCount: number;
  isMarketplace?: boolean;
  sortedLabels: ArticleLabelsList;
  minPrice: Nullable<number>;
  withProminentPricing?: boolean;
  isSmallVariant?: boolean;
}

interface GetRatingsMarginBottom {
  showVariants: boolean;
  showDelivery: boolean;
}

interface ProductCardContentProps extends ProductCardDetailsProps {
  showVariants: boolean;
  showDelivery: boolean;
  ratingMb: SpaceProps['mb'];
}

const prominentPricingOptions = {
  priceComponent: HeadlineH6,
  withMarketingRed: true,
  withLargerEnergyClass: true,
  withLargerOldPrice: true,
};

const StyledArticleSmallPrice = styled(ArticleSmallPrice)<SpaceProps>`
  ${space};
`;

const articleSmallPriceStyles: SpaceProps = {
  mb: ['sp_8', null, null, 'sp_12'],
};

const UnstyledStyledRating = styled(Rating)<any>`
  ${flexWrap};
  ${space};
`;

const StyledRating = (props: ComponentProps<typeof UnstyledStyledRating>) => (
  <UnstyledStyledRating flexWrap="wrap" {...props} />
);

const getRatingsMarginBottom = ({ showVariants, showDelivery }: GetRatingsMarginBottom) => {
  if (showVariants) {
    return ['sp_6', null, null, 'sp_8'];
  }

  return showDelivery ? ['sp_4', null, null, 'sp_6'] : null;
};

const ProductNameSmall = styled(SubtitleST4)`
  ${textWithEllipsis(2)};
`;

// eslint-disable-next-line complexity
const ProductCardContent = ({
  article: {
    sku,
    url,
    name,
    prices: { regular, special, rrp, measurement },
    energyClass,
    ratings: { count, average },
    brand,
    variations,
    bestOffer,
    campaignBadge,
  },
  productIndex,
  visibleProductsCount,
  isMarketplace,
  sortedLabels,
  showVariants,
  showDelivery,
  ratingMb,
  minPrice,
  withProminentPricing,
  isSmallVariant = false,
}: ProductCardContentProps) => {
  const { t } = useLocalization();
  const { matches } = useMatchesViewport();
  const isDesktop = matches.matchesDesktop || matches.matchesLargeDesktop;

  return (
    <Flex flexDirection="column" p={['sp_4', null, null, 'sp_8']} pt="sp_8">
      {regular && (
        <StyledArticleSmallPrice
          {...articleSmallPriceStyles}
          energyClass={energyClass}
          energyClassHref={`${url}${ENERGY_EFFICIENCY_HASH}`}
          specialPrice={special}
          rrpPrice={rrp}
          regularPrice={regular}
          measurement={measurement}
          renderingStrategy={RenderingStrategy.Horizontal}
          {...(withProminentPricing && prominentPricingOptions)}
          labels_v2={sortedLabels}
          isV2={false}
        />
      )}
      {campaignBadge && <CampaignBadge sku={sku} {...campaignBadge} />}
      <NameBlock
        Typography={isSmallVariant && isDesktop ? ProductNameSmall : undefined}
        isHome24={bestOffer?.isHome24}
        name={name}
        isMarketplace={isMarketplace}
        brandName={brand?.name}
      />
      {!!average && (
        <StyledRating
          size={RatingSize.Medium}
          rate={average}
          count={count}
          {...(ratingMb ? { mb: ratingMb } : {})}
        />
      )}

      {showVariants && variations && (
        <ProductVariations
          sku={sku}
          url={url}
          productIndex={productIndex}
          visibleProductsCount={visibleProductsCount}
          showDelivery={showDelivery}
          variations={variations}
          minPrice={minPrice}
          trackingContext={{
            eventName: ProductEvent.ViewClick,
            element: Element.Button,
            interaction: DrawerInteraction.Open,
          }}
        />
      )}
      {showDelivery && (
        <SubtitleST4 color="sys.success.text.default">{t('h24_immediately_available')}</SubtitleST4>
      )}
    </Flex>
  );
};

export const ProductCardDetails = (props: ProductCardDetailsProps) => {
  const { showVariants, showDelivery } = getProductCardDetails(props.article);
  const ratingMb = getRatingsMarginBottom({ showVariants, showDelivery });

  return (
    <ProductCardContent
      {...props}
      showVariants={showVariants}
      showDelivery={showDelivery}
      ratingMb={ratingMb}
    />
  );
};
